import type { FC, FormEvent, ReactNode } from 'react'

import { useId, useState } from 'react'
import { ArrowRight, Check, LoaderCircle } from 'lucide-react'
import { cn } from '@/lib/utils'

type Status = 'idle' | 'loading' | 'success' | 'error'

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/

interface NewsletterFormProps {
    source: string
    className?: string
}

export const NewsletterForm: FC<NewsletterFormProps> = ({ source, className }): ReactNode => {
    const id = useId()
    const [email, setEmail] = useState('')
    const [status, setStatus] = useState<Status>('idle')
    const [message, setMessage] = useState('')

    const onSubmit = async (event: FormEvent<HTMLFormElement>) => {
        event.preventDefault()

        if (!EMAIL_PATTERN.test(email.trim())) {
            setStatus('error')
            setMessage('Please provide your business email.')
            return
        }

        setStatus('loading')
        setMessage('')

        try {
            const response = await fetch('/api/newsletter', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ email: email.trim(), source })
            })

            const data = (await response.json().catch(() => null)) as { message?: string } | null

            if (!response.ok) throw new Error(data?.message ?? 'Something went wrong while subscribing.')

            setStatus('success')
            setMessage('You’re subscribed.')
            setEmail('')
        }
        catch (error) {
            setStatus('error')
            setMessage(error instanceof Error ? error.message : 'Something went wrong while subscribing.')
        }
    }

    return (
        <form onSubmit={onSubmit} noValidate className={cn('w-full max-w-md', className)}>
            <label htmlFor={`${id}-email`} className='sr-only'>
                Business email
            </label>

            <div className='group flex items-end gap-3 border-b border-tone-line pb-3 transition-colors focus-within:border-tone'>
                <input
                    id={`${id}-email`}
                    type='email'
                    name='email'
                    autoComplete='email'
                    placeholder='Your business email'
                    value={email}
                    onChange={event => {
                        setEmail(event.target.value)
                        if (status === 'error') setStatus('idle')
                    }}
                    aria-invalid={status === 'error'}
                    aria-describedby={`${id}-status`}
                    className='min-w-0 flex-1 bg-transparent py-2 text-lg text-tone outline-none placeholder:text-tone-muted/70'
                />

                <button
                    type='submit'
                    disabled={status === 'loading'}
                    data-cursor='Subscribe'
                    className='flex h-11 shrink-0 items-center gap-2 bg-tone px-4 text-sm font-semibold text-tone-bg transition-opacity disabled:opacity-60'
                >
                    {status === 'loading' && <LoaderCircle className='size-4 animate-spin' aria-hidden />}
                    {status === 'success' && <Check className='size-4' aria-hidden />}
                    {status !== 'loading' && status !== 'success' && <ArrowRight className='size-4' aria-hidden />}
                    <span>
                        Subscribe
                    </span>
                </button>
            </div>

            <p
                id={`${id}-status`}
                role='status'
                aria-live='polite'
                className={cn('mt-3 min-h-5 text-sm', status === 'error' ? 'text-ember' : 'text-tone-muted')}
            >
                {message}
            </p>
        </form>
    )
}
